import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Accessibility, 
  ZoomIn, 
  ZoomOut, 
  Contrast, 
  Type, 
  X,
  RotateCcw
} from 'lucide-react';

const AccessibilityWidget = () => {
  const location = useLocation();
  const isMembersArea = location.pathname.startsWith('/members');
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    bigCursor: false,
    highlightLinks: false,
  });

  // Load saved settings
  useEffect(() => {
    const saved = localStorage.getItem('accessibility_settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  // Apply settings to document
  const applySettings = (newSettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // High contrast
    if (newSettings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Big cursor
    if (newSettings.bigCursor) {
      root.classList.add('big-cursor');
    } else {
      root.classList.remove('big-cursor');
    }
    
    // Highlight links
    if (newSettings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
  };

  // Save and apply settings
  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('accessibility_settings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  // Reset all settings
  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      bigCursor: false,
      highlightLinks: false,
    };
    updateSettings(defaultSettings);
  };

  // Increase font size
  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSettings({ ...settings, fontSize: settings.fontSize + 10 });
    }
  };

  // Decrease font size
  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSettings({ ...settings, fontSize: settings.fontSize - 10 });
    }
  };

  // Toggle high contrast
  const toggleHighContrast = () => {
    updateSettings({ ...settings, highContrast: !settings.highContrast });
  };

  // Toggle big cursor
  const toggleBigCursor = () => {
    updateSettings({ ...settings, bigCursor: !settings.bigCursor });
  };

  // Toggle highlight links
  const toggleHighlightLinks = () => {
    updateSettings({ ...settings, highlightLinks: !settings.highlightLinks });
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed z-[90] rounded-full shadow-lg bg-[#00FFD1] text-black flex items-center justify-center hover:bg-[rgba(0,255,209,0.8)] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black ${
          isMembersArea
            ? 'w-10 h-10 md:w-14 md:h-14 left-3 md:left-6 top-[42%] md:top-auto md:bottom-6 md:translate-y-0 -translate-y-1/2 md:transform-none'
            : 'w-14 h-14 bottom-6 left-6'
        }`}
        aria-label="פתח תפריט נגישות"
        aria-expanded={isOpen}
      >
        <Accessibility className={isMembersArea ? "w-5 h-5 md:w-7 md:h-7" : "w-7 h-7"} />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          className={`fixed z-[90] w-72 bg-[#121212] border border-white/10 shadow-2xl ${
            isMembersArea
              ? 'top-[42%] md:top-auto md:bottom-24 left-16 md:left-6 -translate-y-1/2 md:translate-y-0'
              : 'bottom-60 md:bottom-24 left-6'
          }`}
          dir="rtl"
          role="dialog"
          aria-label="תפריט נגישות"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="text-white font-semibold">הגדרות נגישות</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white"
              aria-label="סגור"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Controls */}
          <div className="p-4 space-y-4">
            {/* Font Size */}
            <div>
              <label className="block text-white/80 text-sm mb-2">גודל טקסט</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={decreaseFontSize}
                  disabled={settings.fontSize <= 80}
                  className="w-10 h-10 bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="הקטן טקסט"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white text-center flex-1">{settings.fontSize}%</span>
                <button
                  onClick={increaseFontSize}
                  disabled={settings.fontSize >= 150}
                  className="w-10 h-10 bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="הגדל טקסט"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button
              onClick={toggleHighContrast}
              className={`w-full flex items-center gap-3 p-3 text-right transition-colors ${
                settings.highContrast ? 'bg-[#00FFD1] text-black' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Contrast className="w-5 h-5" />
              <span>ניגודיות גבוהה</span>
            </button>

            {/* Highlight Links */}
            <button
              onClick={toggleHighlightLinks}
              className={`w-full flex items-center gap-3 p-3 text-right transition-colors ${
                settings.highlightLinks ? 'bg-[#00FFD1] text-black' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Type className="w-5 h-5" />
              <span>הדגש קישורים</span>
            </button>

            {/* Reset */}
            <button
              onClick={resetSettings}
              className="w-full flex items-center justify-center gap-2 p-3 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>איפוס הגדרות</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
